import { AxiosResponse } from 'axios';
import { DateTime } from 'luxon';
import { Origin } from 'practice/constants/origin';
import {
  FilterPersonVM,
  FilterPracticeVM,
  PracticeDataVM,
  PracticeVM,
} from 'practice/view-models/practice-research';
import { HttpResponse } from 'shell/models/http-response';
import { PracticeData } from 'shell/models/practice';
import { Operation } from 'shell/view-models/operation';
import { BirthPlace, SearchPerson } from 'shell/view-models/search-anpr';
import { OperationObject } from 'system/utils/operation-object-to-query-string';

export const transformSearchPersonDataset = (values: FilterPersonVM): SearchPerson => {
  let luogoNascita: BirthPlace | undefined = undefined;

  if (values.municipality) {
    luogoNascita = {
      comune: {
        siglaProvinciaIstat: values.municipality.province || undefined,
        nomeComune: values.municipality.description || undefined,
        codiceIstat: values.municipality.istatCode || undefined,
        descrizioneLocalita: values.municipality.locality || undefined,
      },
    };
  }

  if (values.foreignState) {
    luogoNascita = {
      comune: {
        siglaProvinciaIstat: values.foreignState.province || undefined,
        codiceIstat: values.foreignState.istatCode || undefined,
        nomeComune: values.foreignState.value || undefined,
      },
    };
  }

  const transformedFilters: SearchPerson = {
    datiRichiesta: {
      ricercaNazionale: '1', // fixed
    },
    criteriRicerca: {
      sesso: values.gender || undefined,
      codiceFiscale: values.fiscalCode || undefined,
      cognome: values.surname || undefined,
      senzaCognome: !values.surname || values.withoutSurname ? '1' : undefined,
      nome: values.name || undefined,
      senzaNome: !values.name || values.withoutName ? '1' : undefined,
    },
    datiNascita:
      values?.birthDate || luogoNascita
        ? {
            senzaGiorno:
              values?.birthDate &&
              !values.birthChoiceDate?.includes('day') &&
              values.birthChoiceDate?.includes('month')
                ? '1'
                : undefined,
            senzaGiornoMese:
              values?.birthDate &&
              !values?.birthChoiceDate?.includes('day') &&
              !values?.birthChoiceDate?.includes('month')
                ? '1'
                : undefined,
            dataEvento: values?.birthDate?.toFormat('yyyy-MM-dd') || undefined,
            luogoNascita,
          }
        : undefined,
  };
  return transformedFilters;
};

interface HttpResponseListPayload<T> {
  listPratiche: T[];
  sospese?: number;
  bozze?: number;
  totale?: number;
  chiuse?: number;
  aperte?: number;
  daAssegnare?: number;
}

export type CustumPracticeHttpResponseList<T> = HttpResponse<HttpResponseListPayload<T>>;

export const parseProgressivoPratice = (progressivo?: number): string => {
  if (!progressivo) {
    return '';
  }

  let progressivoPratica = `${progressivo}`;

  if ((progressivo?.toString() || '').length < 6) {
    progressivoPratica = `000000${progressivo}`;

    const start = progressivoPratica.length - 6;

    progressivoPratica = progressivoPratica.substring(start);
  }
  return progressivoPratica;
};

export const transformerList = (listResponse: Array<PracticeData>): Array<PracticeVM> => {
  const list: Array<PracticeVM> = listResponse.map((item) => {
    const progressivoPratica = parseProgressivoPratice(item.key?.progressivoPratica);

    const pratice = `${item.key?.tipoRegistro || ''}.${
      item.key?.idAnnoPratica || ''
    }.${progressivoPratica}`;

    // da documentazione query 1
    const dateExchangeStateDate1: DateTime | null = item.timestampCambioStatoQ1
      ? DateTime.fromISO(item.timestampCambioStatoQ1)
      : null;

    // da documentazione query 2
    const dateExchangeStateDate2: DateTime | null = item.timestampCambioStatoQ2
      ? DateTime.fromISO(item.timestampCambioStatoQ2)
      : null;

    const difNowExchangeState1: number =
      DateTime.now()
        .diff(dateExchangeStateDate1 || DateTime.now(), 'hours')
        .toObject().hours || 0;

    const difNowExchangeState2: number =
      DateTime.now()
        .diff(dateExchangeStateDate2 || DateTime.now(), 'hours')
        .toObject().hours || 0;

    let isRedIsFlashes = false;
    let isRed = false;
    let isOrange = false;

    if (difNowExchangeState1 >= (item.metricaVO?.numMaxOreOperazione || 0)) {
      isRedIsFlashes = true;
    } else {
      if (difNowExchangeState2 >= (item.metricaVO?.numMaxOreOperazione || 0)) {
        isRed = true;
      } else {
        if (
          difNowExchangeState1 >= (item.metricaVO?.numOrePerWarning || 0) ||
          difNowExchangeState2 >= (item.metricaVO?.numOrePerWarning || 0)
        ) {
          isOrange = true;
        }
      }
    }

    let isYellow = false;

    if (item.codOperatorePratica !== (item.praticaSttVOList || [])[0]?.codOperatorePratica) {
      isYellow = true;
    }

    return {
      pratice,
      priorita: item.priorita || '',
      type: item.tipoPratica || '',
      dateAcquisition: item.timestampAperturaPratica
        ? DateTime.fromISO(item.timestampAperturaPratica).toFormat('dd/MM/yyyy')
        : '',
      dateClose: item.timestampChiusuraPratica
        ? DateTime.fromISO(item.timestampChiusuraPratica).toFormat('dd/MM/yyyy')
        : '',
      dateExchangeState: item.praticaSttCodStatoVO?.timestampCambioStato
        ? DateTime.fromISO(item.praticaSttCodStatoVO?.timestampCambioStato).toFormat('dd/MM/yyyy')
        : '',
      acquisition: item.dominioPraticaVO?.testoValore || '',
      surname: item.personaVO?.cognome || '',
      name: item.personaVO?.nome || '',
      fiscalCode: item.personaVO?.codFiscale || '',
      state: item.statoPraticaVO?.descrizioneStatoPratica || '',
      dateOpen: item.timestampAperturaPratica
        ? DateTime.fromISO(item.timestampAperturaPratica).toFormat('dd/MM/yyyy')
        : '',
      dateDecorrenza: item.dataDecorrenza
        ? DateTime.fromISO(item.dataDecorrenza).toFormat('dd/MM/yyyy')
        : '',
      lastTypeOperation: item.tipoOperazioneVO?.descrizioneBreveTipoOperazione || '',
      // code to count
      codeState: item.statoPraticaVO?.key?.codiceStatoPratica || '',
      // notifications color
      isRedIsFlashes,
      isRed,
      isOrange,
      isYellow,
      codiceEnte: item.key?.codiceEnte,
      tipoRegistro: item.key?.tipoRegistro,
      idAnnoPratica: item.key?.idAnnoPratica,
      progressivoPratica: item.key?.progressivoPratica,
      idDbLocaleIntestatario: item.idDbLocaleIntestatario || item.personaVO?.key?.idDbLocalePersona,
      idAnprIntestatario: item.idAnprIntestatario,
      idAnprFamiglia: item.famigliaVO?.idAnprFamiglia,
      codOperatorePratica: item.codOperatorePratica,
      operatorName: item.nomeOperatore,
      flagPraticaGestitaInSipo: item.flagPraticaGestitaInSipo,
      origine: item.flagPraticaGestitaInSipo === '1' ? 'SIPo' : 'SIDe',
    };
  });

  return list;
};

export const transformerPraticeList = (
  response: AxiosResponse<CustumPracticeHttpResponseList<PracticeData>>
): PracticeDataVM => {
  const data = response.data.payload.listPratiche || [];

  const list = transformerList(data);

  return {
    list: list || [],
    sospese: response.data.payload.sospese || 0,
    bozze: response.data.payload.bozze || 0,
    chiuse: response.data.payload.chiuse || 0,
    aperte: response.data.payload.aperte || 0,
    totale: response.data.payload.totale || 0,
    daAssegnare: response.data.payload.daAssegnare || 0,
  };
};

export const transformPracticeSearchFiltersData = (data: FilterPracticeVM): OperationObject => {
  // provenienzaItalia '0' o '1' per capire se estero o meno
  // provenienza per mandare il codice del comune o estero o italiano
  // codNazioneProvDest se valorizzato lo stato
  let provenienza = {};

  let provenienzaItalia = {};

  let codNazioneProvDest = {};

  if (data?.origin?.isForeign === Origin.Italian) {
    provenienzaItalia = {
      provenienzaItalia: {
        value: '0',
        operation: Operation.EQUALS,
      },
    };

    if (data?.origin?.municipality?.id) {
      provenienza = {
        provenienza: {
          value: data?.origin?.municipality?.id || '',
          operation: Operation.EQUALS,
        },
      };
    }
  }

  if (data?.origin?.isForeign === Origin.Foreign) {
    provenienzaItalia = {
      provenienzaItalia: {
        value: '1',
        operation: Operation.EQUALS,
      },
    };

    if (data?.origin?.state?.id) {
      codNazioneProvDest = {
        codNazioneProvDest: {
          value: data?.origin?.state?.id || '',
          operation: Operation.EQUALS,
        },
      };
    }

    if (data?.origin?.stateLocation?.id) {
      provenienza = {
        provenienza: {
          value: data?.origin?.stateLocation?.id || '',
          operation: Operation.EQUALS,
        },
      };
    }
  }

  if (data?.origin?.irreperibile) {
    provenienza = {
      provenienza: {
        value: '900',
        operation: Operation.EQUALS,
      },
    };
  }

  let irreperibileFlag = {};

  if (data.irreperibileMilano) {
    irreperibileFlag = {
      irreperibileMilano: {
        value: 'true',
        operation: Operation.EQUALS,
      },
    };
  }

  if (data.irreperibileAltroComune) {
    irreperibileFlag = {
      irreperibileMilano: {
        value: 'false',
        operation: Operation.EQUALS,
      },
    };
  }

  let cittadinoItalianoFlag = {};

  if (data.italianCitizenship) {
    cittadinoItalianoFlag = {
      cittadinoItaliano: {
        value: 'true',
        operation: Operation.EQUALS,
      },
    };
  }

  if (data.foreignCitizenship) {
    cittadinoItalianoFlag = {
      cittadinoItaliano: {
        value: 'false',
        operation: Operation.EQUALS,
      },
    };
  }

  let accertamentoPositivoFlag = {},
    accertamentoNegativoFlag = {},
    accertamentoDubbioFlag = {};

  if (data.accertamentoPositivo) {
    accertamentoPositivoFlag = {
      accertamentoPositivo: {
        value: '1',
        operation: Operation.EQUALS,
      },
    };
  }

  if (data.accertamentoNegativo) {
    accertamentoNegativoFlag = {
      accertamentoNegativo: {
        value: '2',
        operation: Operation.EQUALS,
      },
    };
  }

  if (data.accertamentoDubbio) {
    accertamentoDubbioFlag = {
      accertamentoDubbio: {
        value: '3',
        operation: Operation.EQUALS,
      },
    };
  }

  let prioritaSenzaAssensoOspitante = {};

  if (data.prioritaSenzaAssensoOspitante) {
    prioritaSenzaAssensoOspitante = {
      priorita: {
        value: '1',
        operation: Operation.EQUALS,
      },
    };
  }

  return {
    ...codNazioneProvDest,
    ...provenienzaItalia,
    ...provenienza,
    ...accertamentoPositivoFlag,
    ...accertamentoNegativoFlag,
    ...accertamentoDubbioFlag,
    ...irreperibileFlag,
    ...cittadinoItalianoFlag,
    ...prioritaSenzaAssensoOspitante,
    progressivoAccertamento: {
      value: data?.progressive || '',
      operation: Operation.EQUALS,
    },
    tipoRegistro: {
      value: data?.typeRegister || '',
      operation: Operation.EQUALS,
    },
    codiceOperatore: {
      value: data?.operatorCode || '',
      operation: Operation.EQUALS,
    },
    dataDecorrenzaDa: {
      value: data?.dataDecorrenzaDa?.toFormat('yyyy-MM-dd') || '',
      operation: Operation.EQUALS,
    },
    dataDecorrenzaA: {
      value: data?.dataDecorrenzaA?.toFormat('yyyy-MM-dd') || '',
      operation: Operation.EQUALS,
    },
    idAnnoPratica: { value: data?.years || '', operation: Operation.EQUALS },
    // eslint-disable-next-line camelcase
    progressivoPratica_da: {
      value: data?.fromNumber || '',
      operation: Operation.EQUALS,
    },
    // eslint-disable-next-line camelcase
    progressivoPratica_a: {
      value: data?.atNumber || '',
      operation: Operation.EQUALS,
    },
    // eslint-disable-next-line camelcase
    timestampAperturaPratica_da: {
      value: data?.dateFrom?.toFormat('yyyy-MM-dd') || '',
      operation: Operation.EQUALS,
    },
    // eslint-disable-next-line camelcase
    timestampAperturaPratica_a: {
      value: data?.dateTo?.toFormat('yyyy-MM-dd') || '',
      operation: Operation.EQUALS,
    },
    timestampChiusuraPratica: {
      value: data?.dateClosure?.toFormat('yyyy-MM-dd') || '',
      operation: Operation.EQUALS,
    },
    dataScadenza: { value: data?.dateExpiration?.toFormat('yyyy-MM-dd') || '' },
    // codServizio: { value: data?.service || '', operation: Operation.EQUALS },
    codiceServizioInserimento: {
      value: data?.practicePlacementService || '',
      operation: Operation.EQUALS,
    },
    codOperatorePratica: {
      value: data?.operator || '',
      operation: Operation.EQUALS,
    },
    codiceStatoPratica: {
      value: data?.state || '',
      operation: Operation.EQUALS,
    },
    tipoIniziativa: {
      value: data?.acquisitionSystem || '',
      operation: Operation.EQUALS,
    },
    tipoOperazione: {
      value: data?.lastTypeOperation || '',
      operation: Operation.EQUALS,
    },
    soloAssegnate: {
      value: data?.onlyPracticeAssigned ? '1' : '0',
      operation: Operation.EQUALS,
    },
    numRichiestaAnpr: {
      value: data.numberAnpr || '',
      operation: Operation.EQUALS,
    },
  };
};
